
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { UTILS, POKUTILS } from "../config/API";
import app from "../assets/icons/app.png";
import appConfig, { getPortalKey } from "../config";

let inboxThreadCountCache = null;
let verificationLetterPaymentTypeCache = null;

const OPEN_MENUS_KEY = "sidebarOpenMenus";
const KNOWN_MENUS_KEY = "sidebarKnownMenus";

const readStoredMenuSet = (key) => {
  try {
    const stored = sessionStorage.getItem(key);
    if (stored) return new Set(JSON.parse(stored));
  } catch (error) {
    console.error(`Failed to read ${key} from sessionStorage:`, error);
  }
  return new Set();
};

const Sidebar = ({ onItemSelect }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [openMenus, setOpenMenus] = useState(() => readStoredMenuSet(OPEN_MENUS_KEY));
  const [menuData, setMenuData] = useState(null);
  const [images, setImages] = useState({});
  const [accountType, setAccountType] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [appName, setAppName] = useState("");
  const [selectedSubmenu, setSelectedSubmenu] = useState("");
  const [hasFetchedMenuData, setHasFetchedMenuData] = useState(false); // Track if fetch was attempted
  const maxRetries = 5;
  const [config, setConfig] = useState({});
  const [inboxThreadCount, setInboxThreadCount] = useState(() => inboxThreadCountCache);
  const [verificationLetterPaymentType, setVerificationLetterPaymentType] = useState(
    () => verificationLetterPaymentTypeCache
  );

  // Function to check if expiration date has passed
  const isExpirationDatePassed = useCallback((expirationDate) => {
    if (!expirationDate) return true; // If no date, consider it expired

    try {
      const [month, day, year] = expirationDate.split('/').map(Number);
      const expiration = new Date(year, month - 1, day); // month is 0-indexed
      const currentDate = new Date();

      return currentDate > expiration;
    } catch (error) {
      console.error("Error parsing expiration date:", error);
      return true; // If there's an error parsing, consider it expired
    }
  }, []);

  // Initialize config and settings
  useEffect(() => {
    const key = getPortalKey();
    setConfig(appConfig[key] || appConfig.portal);
  }, []);

  // Initialize config and app name
  useEffect(() => {
    const key = getPortalKey();
    const config = appConfig[key] || appConfig.portal;
    if (config) {
      setAppName(config.name);
    }
  }, []);

  // Update selected submenu based on current URL
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const currentTab = urlParams.get('tab');

    if (currentTab) {
      setSelectedSubmenu(currentTab);
    } else {
      const path = location.pathname;
      setSelectedSubmenu(path.split('/').pop() || "");
    }
  }, [location]);

  const toggleMenu = useCallback((menuName) => {
    setOpenMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuName)) {
        newSet.delete(menuName);
      } else {
        newSet.add(menuName);
      }
      return newSet;
    });
  }, []);

  // Persist open/closed menu state so navigating between pages (which remounts
  // the sidebar) doesn't reset it and re-trigger the expand animation.
  useEffect(() => {
    try {
      sessionStorage.setItem(OPEN_MENUS_KEY, JSON.stringify([...openMenus]));
    } catch (error) {
      console.error("Failed to persist open menus:", error);
    }
  }, [openMenus]);

  const handleHomeClick = useCallback(() => {
    navigate("/home");
    onItemSelect?.();
    setSelectedSubmenu("");
  }, [navigate, onItemSelect]);

  const handleSubmenuClick = useCallback((submenu, link) => {
    const isUrl = /^https?:\/\//.test(link);
    const isVerificationLetter = submenu === "Verification Letter";

    if (isVerificationLetter) {
      setSelectedSubmenu(submenu);
      navigate(`/page?itype=verificationletter&tab=${encodeURIComponent(submenu)}`);
      onItemSelect?.();
      return;
    }

    setSelectedSubmenu(submenu);

    if (isUrl) {
      window.open(link, submenu);
    } else {
      const hasQuery = link.includes('?');
      const newUrl = hasQuery
        ? `${link}&tab=${encodeURIComponent(submenu)}`
        : `${link}?tab=${encodeURIComponent(submenu)}`;

      const currentUrl = window.location.href;
      const isSameTab = currentUrl.includes(`tab=${encodeURIComponent(submenu)}`) ||
        (currentUrl.endsWith(link) && !currentUrl.includes('tab=') && !submenu);

      if (isSameTab) {
        window.location.reload();
      } else if (link) {
        navigate(newUrl);
      }
    }

    onItemSelect?.();
  }, [navigate, onItemSelect]);

  const xmlToJson = useCallback((xml) => {
    const result = {};
    const processNode = (node, obj) => {
      if (node.nodeType === 1) {
        const nodeName = node.nodeName;
        const childObj = {};

        if (node.attributes.length > 0) {
          childObj["@attributes"] = {};
          for (let i = 0; i < node.attributes.length; i++) {
            const attr = node.attributes[i];
            childObj["@attributes"][attr.nodeName] = attr.nodeValue;
          }
        }

        for (let i = 0; i < node.childNodes.length; i++) {
          processNode(node.childNodes[i], childObj);
        }

        if (obj[nodeName]) {
          if (!Array.isArray(obj[nodeName])) obj[nodeName] = [obj[nodeName]];
          obj[nodeName].push(childObj);
        } else {
          obj[nodeName] = childObj;
        }
      } else if (node.nodeType === 3 && node.nodeValue.trim() !== "") {
        obj["#text"] = node.nodeValue.trim();
      }
    };

    processNode(xml.documentElement, result);
    return result;
  }, []);

  const fetchMenuData = useCallback(async () => {
    if (isLoading || hasFetchedMenuData) return; // Prevent multiple calls
    setIsLoading(true);

    try {
      const response = await fetch(UTILS, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json"
        },
        body: new URLSearchParams({ action: "menu" }),
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const xmlText = await response.text();
      const xmlDoc = new DOMParser().parseFromString(xmlText, "application/xml");
      const jsonData = xmlToJson(xmlDoc);

      const storedMenuData = sessionStorage.getItem("menuData");
      const stringifiedNewData = JSON.stringify(jsonData, null, 2);

      console.log("stringifiedNewData", stringifiedNewData);

      if (storedMenuData !== stringifiedNewData) {
        sessionStorage.setItem("menuData", stringifiedNewData);
        setMenuData(jsonData);
      }

      setHasFetchedMenuData(true); // Mark as fetched
    } catch (error) {
      console.error("Failed to fetch menu data:", error);
      setHasFetchedMenuData(true); // Mark as fetched even on error to prevent retries
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, xmlToJson, hasFetchedMenuData]); // Add hasFetchedMenuData to dependencies

  // Helper function to check if showMenu=false exists in args
  const shouldHideMenu = useCallback((args) => {
    if (!args) return false;
    // Split by comma and check each parameter
    const params = args.split(',').map(param => param.trim());
    return params.includes("showMenu=false");
  }, []);

  // Helper function to extract icon path from args (first part before comma)
  const extractIconPath = useCallback((args) => {
    if (!args) return "";
    // Get the first part before comma (the icon path)
    const parts = args.split(',').map(part => part.trim());
    return parts[0] || "";
  }, []);

  const transformMenuData = useCallback((data) => {
    if (!data?.fcnode?.fcnode) return [];

    const currentAccountType = accountType || sessionStorage.getItem("accountType");

    const menuNodes = Array.isArray(data.fcnode.fcnode)
      ? data.fcnode.fcnode
      : [data.fcnode.fcnode];

    return menuNodes
      .map((menu) => {
        // Check if menu is null or doesn't have attributes
        if (!menu || !menu["@attributes"]) return null;

        // Check if menu should be hidden based on args
        const menuArgs = menu["@attributes"].args || "";
        if (shouldHideMenu(menuArgs)) {
          return null; // Hide this menu item
        }

        let submenus = [];

        if (menu.fcnode) {
          const submenuNodes = Array.isArray(menu.fcnode)
            ? menu.fcnode
            : [menu.fcnode];

          submenus = submenuNodes
            .map((submenu) => {
              // Check if submenu is null or doesn't have attributes
              if (!submenu || !submenu["@attributes"]) return null;

              const submenuName = submenu["@attributes"].name;
              const submenuArgs = submenu["@attributes"].args || "";

              if (
                config.name === "Pokagon Band of Potawatomi" &&
                submenuName === "Verification Letter"
              ) {
                if (!verificationLetterPaymentType) {
                  return null;
                }

                return {
                  name: submenuName,
                  link: "/page?itype=verificationletter",
                  tooltip: submenu["@attributes"].tooltip || submenuName,
                  winname: submenu["@attributes"].winname || submenuName,
                };
              }

              // Check if submenu should be hidden based on args
              if (shouldHideMenu(submenuArgs)) {
                return null; // Hide this submenu item
              }

              return {
                name: submenuName,
                link: submenu["@attributes"].link,
                tooltip: submenu["@attributes"].tooltip || submenuName,
                winname: submenu["@attributes"].winname || submenuName
              };
            })
            .filter(submenu => submenu !== null); // Remove null submenus

          if (config.name === "Prairie Band Potawatomi Nation") {
            const taxPayerType = localStorage.getItem("selectedTaxPayerType");
            const businessLicenseType = localStorage.getItem("businessLicenseType");
            const businessLicenseStatus = localStorage.getItem("businessLicenseStatus");
            const expirationDate = localStorage.getItem("businessLicenseExpirationDate");
            console.log("Business License Expiration Date from localStorage:", expirationDate);

            // Convert comma-separated string to array and trim each item
            const licenseTypes = businessLicenseType ? businessLicenseType.split(',').map(type => type.trim()) : [];


            if (licenseTypes.length === 0 && menu["@attributes"].name === "Tax Return") {

              return null; // Return null to filter out this menu
            }
            if (expirationDate === "" && menu["@attributes"].name === "Tax Return") {

              return null;
            }
            if (businessLicenseStatus !== "Approved" && menu["@attributes"].name === "Tax Return") {

              return null;
            }


            if (expirationDate && menu["@attributes"].name === "Tax Return") {
              if (isExpirationDatePassed(expirationDate)) {
                console.log("Business license has expired. Hiding Tax Return menu.");
                return null;
              }
            }

            if (taxPayerType === "Retailer") {
              submenus = submenus.filter(submenu =>
                submenu.name !== "Distributor – Stamp Inventory" &&
                submenu.name !== "Distributor - Unstamped Packs" &&
                submenu.name !== "Distributor - Off-Reservation Packs" &&
                submenu.name !== "Distributor – NPM Sales" &&
                submenu.name !== "Distributor – Stamps, Packs" &&
                submenu.name !== "Distributor Fuel" &&
                submenu.name !== "Distributor – Packs"
              );
            }

            if (taxPayerType === "Distributor") {
              submenus = submenus.filter(submenu =>
                submenu.name !== "Retailer - Packs" &&
                submenu.name !== "Retailer - Sales Tax" &&
                submenu.name !== "Retailer – Annual Packs Report" &&
                submenu.name !== "Retailer Fuel" &&
                submenu.name !== "Retailer Sales Tax - Casino"
              );
            }

            if (!licenseTypes.includes("Fuel")) {
              submenus = submenus.filter(submenu =>
                submenu.name !== "Retailer Fuel" &&
                submenu.name !== "Distributor Fuel"
              );
            }
            if (!licenseTypes.includes("Casino")) {
              submenus = submenus.filter(submenu => submenu.name !== "Retailer Sales Tax - Casino");
            }
            // else {
            //   submenus = submenus.filter(submenu => submenu.name !== "Retailer - Sales Tax");
            // }

            if (!licenseTypes.includes("Tobacco")) {
              submenus = submenus.filter(submenu =>
                submenu.name !== "Distributor – Stamp Inventory" &&
                submenu.name !== "Distributor - Unstamped Packs" &&
                submenu.name !== "Distributor - Off-Reservation Packs" &&
                submenu.name !== "Distributor – NPM Sales" &&
                submenu.name !== "Distributor – Stamps, Packs" &&
                submenu.name !== "Distributor – Packs" &&
                submenu.name !== "Retailer - Packs" &&
                submenu.name !== "Retailer – Annual Packs Report"

              );
            }
          }
        }

        return {
          name: menu["@attributes"].name,
          icon: extractIconPath(menu["@attributes"].args),
          tooltip: menu["@attributes"].tooltip || menu["@attributes"].name,
          submenus,
        };
      })
      .filter(menu => menu !== null) // Remove null menus
      .map((menu) => {
        const isMyForms =
          ["TERO", "Klamath Tribes"].includes(appName) &&
          menu.name === "My Forms" &&
          ["Staff", "Contractor", "TERO Worker"].includes(currentAccountType);

        if (isMyForms) {
          let allowedMenus = [];
          console.log("currentAccountType", currentAccountType);

          if (currentAccountType === "Staff") {
            allowedMenus = ["Site Checks", "Items Received"];
          } else if (currentAccountType === "Contractor") {
            allowedMenus = ["Compliance Plan", "Compliance Plan Adjustments", "Employment Verification", "Employee Termination", "TERO Worker Evaluation"];
          } else if (currentAccountType === "TERO Worker") {
            allowedMenus = ["Request for Services", "Company Evaluation", "New Intake"];
          }

          menu.submenus = menu.submenus.filter((submenu) =>
            allowedMenus.includes(submenu.name)
          );
        }

        const isTeroProfile = appName === "TERO" && menu.name === "About Me";
        if (isTeroProfile && Array.isArray(menu.submenus)) {
          // Show "Check In" only for TERO Workers
          if (currentAccountType !== "TERO Worker") {
            menu.submenus = menu.submenus.filter((submenu) => submenu.name !== "Check In");
          }
        }

        return menu;
      })
      .filter((menu) => {
        const isMyForms =
          appName === "TERO" &&
          menu.name === "My Forms" &&
          ["Staff", "Contractor", "TERO Worker"].includes(currentAccountType);

        if (isMyForms) {
          return menu.submenus.length > 0;
        }

        return true;
      });
  }, [accountType, appName, isExpirationDatePassed, shouldHideMenu, config, extractIconPath, verificationLetterPaymentType]);


  // Add this useEffect to your Sidebar component
  useEffect(() => {
    const handleStorageChange = () => {
      // Clear menu data and refetch when businessLicenseType changes
      sessionStorage.removeItem("menuData");
      setHasFetchedMenuData(false);
      setMenuData(null);
    };

    // Listen for storage events
    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom event (for same-tab updates)
    window.addEventListener('localStorageChange', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleStorageChange);
    };
  }, []);

  const fetchInboxThreadCount = useCallback(async () => {
    if (config.name !== "Pokagon Band of Potawatomi") return;

    try {
      const response = await fetch(POKUTILS, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: new URLSearchParams({ action: "threads" }),
      });

      const data = await response.json();

      if (!response.ok) return;

      if (Array.isArray(data)) {
        inboxThreadCountCache = data.length;
        setInboxThreadCount(data.length);
      } else if (data?.status !== "error") {
        inboxThreadCountCache = 0;
        setInboxThreadCount(0);
      }
    } catch (error) {
      console.error("Failed to fetch inbox thread count:", error);
    }
  }, [config.name]);

  useEffect(() => {
    if (config.name === "Pokagon Band of Potawatomi") {
      fetchInboxThreadCount();
    }
  }, [config.name, fetchInboxThreadCount]);

  const fetchRecentPaymentType = useCallback(async () => {
    if (config.name !== "Pokagon Band of Potawatomi") return;

    try {
      const response = await fetch(POKUTILS, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
        },
        body: new URLSearchParams({
          action: "findrecentpaymenttype",
        }),
      });

      if (!response.ok) return;

      const data = await response.json();

      if (data?.status === "success" && data?.paymenttype) {
        verificationLetterPaymentTypeCache = data.paymenttype;
        setVerificationLetterPaymentType(data.paymenttype);
        sessionStorage.setItem("verificationLetterPaymentType", data.paymenttype);
        if (data.documenturl) {
          sessionStorage.setItem("verificationLetterDocumentUrl", data.documenturl);
        } else {
          sessionStorage.removeItem("verificationLetterDocumentUrl");
        }
      } else {
        verificationLetterPaymentTypeCache = null;
        setVerificationLetterPaymentType(null);
        sessionStorage.removeItem("verificationLetterPaymentType");
        sessionStorage.removeItem("verificationLetterDocumentUrl");
      }
    } catch (error) {
      console.error("Failed to fetch recent payment type:", error);
    }
  }, [config.name]);

  useEffect(() => {
    if (config.name === "Pokagon Band of Potawatomi") {
      fetchRecentPaymentType();
    }
  }, [config.name, fetchRecentPaymentType]);

  // Fetch account type
  useEffect(() => {
    const storedAccountType = sessionStorage.getItem("accountType");
    if (storedAccountType) {
      setAccountType(storedAccountType);
    } else if (retryCount < maxRetries) {
      const timer = setTimeout(() => {
        setRetryCount(prev => prev + 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [retryCount]);

  // Fetch menu data only once when component mounts
  useEffect(() => {
    const storedMenuData = sessionStorage.getItem("menuData");
    if (storedMenuData) {
      try {
        const parsedData = JSON.parse(storedMenuData);
        setMenuData(parsedData);
        setHasFetchedMenuData(true); // Mark as fetched since we have data
      } catch (e) {
        sessionStorage.removeItem("menuData");
        // Only fetch if we haven't already attempted to fetch
        if (!hasFetchedMenuData) {
          fetchMenuData();
        }
      }
    } else {
      // Only fetch if we haven't already attempted to fetch
      if (!hasFetchedMenuData) {
        fetchMenuData();
      }
    }
  }, [fetchMenuData, hasFetchedMenuData]); // Add hasFetchedMenuData to dependencies

  const menuItems = useMemo(() => {
    if (!menuData) return [];
    return transformMenuData(menuData);
  }, [menuData, transformMenuData]);

  // Menus default to open, but only the first time a given menu is seen.
  // After that we respect the persisted open/closed state so navigation
  // doesn't re-open (and re-animate) menus the user has collapsed.
  useEffect(() => {
    if (!menuItems.length) return;

    const currentNames = menuItems.map((item) => item.name);
    const knownMenus = readStoredMenuSet(KNOWN_MENUS_KEY);
    const newMenus = currentNames.filter((name) => !knownMenus.has(name));

    if (newMenus.length > 0) {
      setOpenMenus((prev) => {
        const next = new Set(prev);
        newMenus.forEach((name) => next.add(name));
        return next;
      });
    }

    try {
      sessionStorage.setItem(KNOWN_MENUS_KEY, JSON.stringify(currentNames));
    } catch (error) {
      console.error("Failed to persist known menus:", error);
    }
  }, [menuItems]);

  useEffect(() => {
    const loadImages = async () => {
      if (!menuItems.length) return;

      const newImages = {};
      let needsUpdate = false;

      for (const item of menuItems) {
        if (!images[item.icon]) {
          try {
            const image = await import(`../assets/icons/${item.icon}`);
            newImages[item.icon] = image.default;
            needsUpdate = true;
          } catch (error) {
            console.error(`Failed to load image: ${item.icon}`, error);
            newImages[item.icon] = app;
            needsUpdate = true;
          }
        }
      }

      if (needsUpdate) {
        setImages(prev => ({ ...prev, ...newImages }));
      }
    };

    loadImages();
  }, [menuItems, images]);

  if (!appName || !accountType || !menuData) {
    return (
      <div
        className={`flex flex-col w-64 bg-white text-black h-full sticky top-0 overflow-hidden
          ${appName === "Colorado River Indian Tribes" ? "lg:pt-6" : "lg:pt-16"} px-4 pb-4`}
      >
        {/* Title placeholder */}
        <div className="mt-5 mb-6 h-6 w-40 animate-pulse rounded-md bg-gray-200" />

        {/* Menu group placeholders */}
        <div className="space-y-6">
          {[0, 1, 2].map((group) => (
            <div key={group} className="space-y-3">
              {/* Menu header: icon + label + chevron */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-5 w-5 animate-pulse rounded bg-gray-200" />
                  <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
                </div>
                <div className="h-3 w-3 animate-pulse rounded bg-gray-200" />
              </div>

              {/* Submenu lines */}
              <div className="space-y-2 pl-7">
                {[0, 1, 2].map((row) => (
                  <div
                    key={row}
                    className="h-3.5 animate-pulse rounded bg-gray-100"
                    style={{ width: `${70 - row * 12}%` }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col w-64 bg-white text-black h-full sticky top-0 overflow-hidden
        ${appName === "Colorado River Indian Tribes" ? "lg:pt-6" : "lg:pt-16"} px-4 pb-4`}
    >
      <button
        onClick={handleHomeClick}
        className="mt-5 mb-3 px-1 text-left text-lg font-bold tracking-tight text-gray-800 transition-colors hover:text-primary"
      >
        {appName === "TERO" ? "My TERO" : appName === "Colorado River Indian Tribes" ? "Taxpayer Portal Home" : appName}
      </button>

      <div className="mb-3 h-px bg-gray-100" />

      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        <ul className="space-y-1 pb-2">
          {menuItems.map((item) => {
            const isMenuOpen = openMenus.has(item.name);
            return (
            <li key={item.name}>
              <div
                className={`group flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 cursor-pointer transition-colors ${isMenuOpen
                  ? 'bg-gray-50 text-gray-900'
                  : 'text-gray-700 hover:bg-primary/10 hover:text-primary'
                  }`}
                onClick={() => toggleMenu(item.name)}
                title={item.tooltip}
              >
                <div className="flex min-w-0 items-center gap-2.5">
                  <img
                    src={images[item.icon] || app}
                    alt={item.name}
                    className="h-5 w-5 shrink-0 opacity-80 transition-opacity group-hover:opacity-100"
                  />
                  <span className="truncate font-semibold">{item.name}</span>
                </div>
                <svg
                  className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-300 group-hover:text-primary ${isMenuOpen ? 'rotate-90' : 'rotate-0'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>

              <div
                className={`transition-all duration-300 ease-in-out ${isMenuOpen
                  ? 'max-h-[60vh] overflow-y-auto'
                  : 'max-h-0 overflow-hidden'
                  }`}
              >
                <ul className="ml-4 mt-1 space-y-0.5 border-l border-gray-100 pl-3">
                  {item.submenus.map((submenu) => {
                    const isSelected = selectedSubmenu === (submenu.winname || submenu.name);
                    return (
                      <li
                        key={submenu.name}
                        className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm cursor-pointer transition-colors ${isSelected
                          ? 'bg-primary font-medium text-white shadow-sm'
                          : 'text-gray-600 hover:bg-primary/10 hover:text-primary'
                          }`}
                        onClick={() => handleSubmenuClick(submenu.winname || submenu.name, submenu.link)}
                        title={submenu.tooltip}
                      >
                        <span className="min-w-0 flex-1 break-words">{submenu.name}</span>
                        {config.name === "Pokagon Band of Potawatomi" &&
                          submenu.name === "Inbox" &&
                          inboxThreadCount !== null &&
                          inboxThreadCount > 0 && (
                            <span className={`flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-xs font-semibold ${isSelected ? 'bg-white text-primary' : 'bg-red-500 text-white'}`}>
                              {inboxThreadCount}
                            </span>
                          )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;